import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { GlassCard } from './Layout';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { ShieldCheck, Upload, Download, Trash2, FileText, Lock, BadgeCheck, Loader2 } from 'lucide-react';
import { AiCostHint, AiPauseNotice } from './AiCredits';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = {
  resume: 'Resume or CV', certificate: 'Certificate or credential', license: 'Licence',
  transcript: 'Transcript', award: 'Award or commendation', evaluation: 'Evaluation or reference',
  portfolio: 'Project or portfolio piece',
};

export const ProofOfSkill = ({ onSkillsChanged }) => {
  const { getAuthHeader } = useAuth();
  const fileRef = useRef(null);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ kind: 'certificate', title: '', issuer: '', skill: '' });
  const [suggestions, setSuggestions] = useState([]);
  const [soft, setSoft] = useState(null);

  const load = useCallback(async () => {
    const [res, sk] = await Promise.all([
      axios.get(`${API}/proof`, getAuthHeader()),
      axios.get(`${API}/assessment/soft-skills`, getAuthHeader()).catch(() => ({ data: { available: false, skills: [] } })),
    ]);
    setD(res.data);
    setSoft(sk.data?.available ? sk.data : null);
  }, [getAuthHeader]);

  const addSoftSkills = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/skills/accept-suggestions`,
        { skills: soft.skills.map(s => ({ name: s.name, category: s.category, evidence: s.evidence })) },
        getAuthHeader());
      toast.success(`Added ${soft.skills.length} skills your assessment evidences.`);
      await load();
      onSkillsChanged?.();
    } catch { toast.error('Could not add those skills.'); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setSuggestions([]);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', form.kind);
      fd.append('title', form.title || file.name);
      fd.append('issuer', form.issuer);
      fd.append('skill', form.skill);
      const res = await axios.post(`${API}/proof/upload`, fd, {
        ...getAuthHeader(),
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      toast.success(res.data.note || 'Uploaded.');
      if (res.data.suggestions?.length) setSuggestions(res.data.suggestions);
      setForm({ ...form, title: '', issuer: '', skill: '' });
      await load();
      onSkillsChanged?.();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : det?.message || 'Upload failed. Try again.');
      if (det?.code === 'ai_paused') await load();
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const acceptSuggestions = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/skills/accept-suggestions`, { skills: suggestions }, getAuthHeader());
      toast.success(`Saved ${suggestions.length} skills from your resume.`);
      setSuggestions([]);
      await load();
      onSkillsChanged?.();
    } catch { toast.error('Could not save those skills.'); }
    finally { setBusy(false); }
  };

  const download = async (doc) => {
    const res = await axios.get(`${API}/proof/document/${doc.id}`, { ...getAuthHeader(), responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = doc.filename || doc.name; a.click();
    URL.revokeObjectURL(url);
  };

  const remove = async (id) => {
    const res = await axios.delete(`${API}/proof/document/${id}`, getAuthHeader());
    setD(res.data);
    onSkillsChanged?.();
    toast.success('Deleted, the file and the proof it backed are gone.');
  };

  if (!d) return null;

  return (
    <div className="space-y-6">
      <AiPauseNotice />
      {/* Upload */}
      <GlassCard className="fp-rail" data-testid="proof-upload-card">
        <h2 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
          <Upload className="w-5 h-5 text-cyan-300" />Upload instead of typing
        </h2>
        <p className="text-sm text-slate-300 mt-1">
          Drop in a resume and we pull the skills out. Drop in a certificate, licence or transcript and it becomes
          proof attached to a skill.
        </p>

        <div className="grid md:grid-cols-4 gap-3 mt-4">
          <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}
            className="input-dark" data-testid="proof-kind-select">
            {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="What is it called?" data-testid="proof-title-input" />
          {form.kind !== 'resume' && (
            <>
              <Input value={form.issuer} onChange={e => setForm({ ...form, issuer: e.target.value })}
                placeholder="Who issued it?" data-testid="proof-issuer-input" />
              <Input value={form.skill} onChange={e => setForm({ ...form, skill: e.target.value })}
                placeholder="Which skill does it prove?" data-testid="proof-skill-input" />
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          <input ref={fileRef} type="file" className="hidden" data-testid="proof-file-input"
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt"
            onChange={e => upload(e.target.files?.[0])} />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-primary" data-testid="proof-choose-file-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reading your file…</> : <><Upload className="w-4 h-4 mr-2" />Choose a file</>}
          </Button>
          <span className="text-xs text-slate-300">PDF, image or document · under 12 MB · stored privately against your account only</span>
          {form.kind === 'resume' && <AiCostHint action="resume_extraction" label="reading your resume" />}
        </div>

        {suggestions.length > 0 && (
          <div className="mt-5 p-4 rounded-xl border border-emerald-400/40 bg-emerald-500/[0.07]" data-testid="proof-suggestions">
            <p className="text-sm text-white">Found {suggestions.length} skills in that resume, tap any to remove, then save.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {suggestions.map(s => (
                <button key={s.name} onClick={() => setSuggestions(l => l.filter(x => x.name !== s.name))}
                  title={s.evidence || ''} data-testid={`proof-suggestion-${s.name}`}
                  className="px-3 py-1.5 rounded-full text-xs border border-emerald-400/40 bg-emerald-400/10 text-emerald-100 hover:border-rose-400/60 transition-colors duration-200">
                  {s.name} ✕
                </button>
              ))}
            </div>
            <Button onClick={acceptSuggestions} disabled={busy} className="btn-primary mt-4" data-testid="proof-accept-btn">
              Save these {suggestions.length} skills
            </Button>
          </div>
        )}
      </GlassCard>

      {/* From the assessment, the soft skills nobody thinks to claim */}
      {soft?.skills?.length > 0 && (
        <GlassCard className="fp-rail" data-testid="assessment-soft-skills">
          <h2 className="font-outfit text-lg font-semibold text-white flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-violet-300" />From your assessment
          </h2>
          <p className="text-sm text-slate-300 mt-1">
            Your {soft.code} code and trait scores evidence these human skills. {soft.note}
          </p>
          <div className="mt-4 space-y-2">
            {soft.skills.map(s => (
              <div key={s.name} className="fp-tile px-3 py-2" data-testid={`soft-skill-${s.name}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-white flex-1 min-w-[180px]">{s.name}</span>
                  <span className="text-[10px] uppercase tracking-wider text-violet-300">{s.category}</span>
                </div>
                <p className="text-[11px] text-slate-300 mt-0.5">{s.why} <span className="text-slate-300">({s.evidence})</span></p>
              </div>
            ))}
          </div>
          <Button className="btn-primary mt-4" onClick={addSoftSkills} disabled={busy} data-testid="add-soft-skills-btn">
            Add these {soft.skills.length} to my skills
          </Button>
        </GlassCard>
      )}

      {/* Proof levels */}
      <GlassCard data-testid="proof-levels-card">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-cyan-300" />Proof of Skill
            </h2>
            <p className="text-sm text-slate-300 mt-1">{d.note}</p>
          </div>
          <div className="flex gap-6">
            <div className="text-right">
              <p className="text-2xl font-bold text-white fp-num" data-testid="avg-proof-level">{d.average_level}</p>
              <p className="text-[11px] uppercase tracking-wider text-slate-300">Avg level</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-cyan-300 fp-num">{d.proven_count}</p>
              <p className="text-[11px] uppercase tracking-wider text-slate-300">Documented+</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-emerald-300 fp-num">{d.verified_count}</p>
              <p className="text-[11px] uppercase tracking-wider text-slate-300">Credentialed+</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-5">
          {d.levels.map(l => (
            <span key={l.level} title={l.meaning} className="fp-pill" style={{ borderColor: `${l.hue}66`, color: l.hue }}>
              {l.level} · {l.label}
            </span>
          ))}
        </div>

        <div className="mt-5 space-y-2" data-testid="proof-skill-rows">
          {d.skills.length === 0 && <p className="text-sm text-slate-300">No skills on your profile yet, upload a resume above and they'll appear here.</p>}
          {d.skills.map(s => (
            <div key={s.name} className="fp-tile px-4 py-3" data-testid={`proof-row-${s.name}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-white flex-1 min-w-[160px]">{s.name}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold text-slate-900"
                  style={{ background: s.hue }}>L{s.level} · {s.label}</span>
                <span className="text-[11px] text-slate-300">{s.evidence_count} piece{s.evidence_count === 1 ? '' : 's'} of proof</span>
              </div>
              <div className="fp-meter mt-2">
                <div className="fp-meter-fill" style={{ width: `${(s.level / 7) * 100}%`, background: s.hue, boxShadow: `0 0 12px ${s.hue}99` }} />
              </div>
              {s.how_to_level_up && (
                <p className="text-[11px] text-slate-300 mt-1.5">
                  <span className="text-slate-300">To reach {s.next_level}, </span>{s.how_to_level_up}
                </p>
              )}
            </div>
          ))}
        </div>
      </GlassCard>

      {/* Documents + safety */}
      <div className="grid lg:grid-cols-2 gap-6">
        <GlassCard data-testid="proof-documents-card">
          <h2 className="font-outfit text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-violet-300" />Your documents
          </h2>
          <p className="text-sm text-slate-300 mt-1">Download or delete anything you've given us, any time.</p>
          <div className="mt-4 space-y-2">
            {d.documents.length === 0 && <p className="text-sm text-slate-300">Nothing uploaded yet.</p>}
            {d.documents.map(doc => (
              <div key={doc.id} className="fp-tile px-3 py-2 flex items-center gap-3" data-testid={`doc-${doc.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{doc.name}</p>
                  <p className="text-[11px] text-slate-300">
                    {KIND_LABEL[doc.doc_type] || doc.doc_type} · {Math.round((doc.file_size || 0) / 1024)} KB · {new Date(doc.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
                <button onClick={() => download(doc)} data-testid={`download-${doc.id}`}
                  className="text-cyan-300 hover:text-cyan-200 transition-colors duration-200" title="Download">
                  <Download className="w-4 h-4" />
                </button>
                <button onClick={() => remove(doc.id)} data-testid={`delete-doc-${doc.id}`}
                  className="text-slate-300 hover:text-rose-400 transition-colors duration-200" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="fp-rail fp-rail-cool" data-testid="proof-privacy-card">
          <h2 className="font-outfit text-lg font-semibold text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-300" />{d.privacy.headline}
          </h2>
          <ul className="mt-4 space-y-2">
            {d.privacy.points.map((p, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-300">
                <Lock className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" />{p}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2 mt-5">
            {d.privacy.actions.map(a => (
              <Link key={a.path} to={a.path} className="fp-pill" data-testid={`privacy-link-${a.path}`}>{a.label}</Link>
            ))}
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default ProofOfSkill;
